export type PredictionOutcome =
  | 'open'
  | 'predicted'
  | 'unpredicted'
  | 'exact'
  | 'correct'
  | 'wrong'
  | 'missed';

interface PredictionScores {
  homeGoals: number;
  awayGoals: number;
}

export function hasMatchResult(
  homeGoals?: number | null,
  awayGoals?: number | null
): boolean {
  return homeGoals !== undefined && homeGoals !== null && awayGoals !== undefined && awayGoals !== null;
}

export function getPredictionOutcome(
  homeGoals: number | null | undefined,
  awayGoals: number | null | undefined,
  prediction: PredictionScores | null | undefined,
  predictionsOpen: boolean
): PredictionOutcome {
  if (predictionsOpen) {
    return prediction ? 'predicted' : 'open';
  }

  if (!hasMatchResult(homeGoals, awayGoals)) {
    return prediction ? 'predicted' : 'unpredicted';
  }

  if (!prediction) {
    return 'missed';
  }

  const isExactScore =
    homeGoals === prediction.homeGoals && awayGoals === prediction.awayGoals;

  if (isExactScore) {
    return 'exact';
  }

  const isCorrectResult =
    (homeGoals! > awayGoals! && prediction.homeGoals > prediction.awayGoals) ||
    (homeGoals! < awayGoals! && prediction.homeGoals < prediction.awayGoals) ||
    (homeGoals === awayGoals && prediction.homeGoals === prediction.awayGoals);

  return isCorrectResult ? 'correct' : 'wrong';
}

const SCORED_OUTCOME_CARD_CLASS: Partial<Record<PredictionOutcome, string>> = {
  exact: 'card-outcome-exact',
  correct: 'card-outcome-correct',
  wrong: 'card-outcome-wrong',
};

export function getOutcomeCardClasses(outcome: PredictionOutcome, hasPrediction: boolean): string {
  const scoredClass = SCORED_OUTCOME_CARD_CLASS[outcome];
  if (scoredClass) {
    return scoredClass;
  }

  return hasPrediction
    ? 'bg-green-50 dark:bg-gray-800/80'
    : 'bg-gray-50 dark:bg-gray-800/50';
}

export function isScoredOutcome(outcome: PredictionOutcome): boolean {
  return outcome === 'exact' || outcome === 'correct' || outcome === 'wrong';
}

export function getOutcomeLabel(outcome: PredictionOutcome): string | null {
  switch (outcome) {
    case 'exact':
      return 'Exact score';
    case 'correct':
      return 'Correct result';
    case 'wrong':
      return 'Incorrect';
    case 'missed':
      return 'No prediction';
    default:
      return null;
  }
}

export function getOutcomeAccentColor(outcome: PredictionOutcome): string {
  switch (outcome) {
    case 'exact':
      return 'text-amber-950 dark:text-amber-950';
    case 'correct':
      return 'text-green-900 dark:text-green-50';
    case 'wrong':
      return 'text-red-900 dark:text-red-50';
    case 'missed':
      return 'text-gray-700 dark:text-gray-300';
    default:
      return 'text-[var(--text-primary)]';
  }
}

export function getOutcomeBorderColor(outcome: PredictionOutcome): string {
  if (isScoredOutcome(outcome)) {
    switch (outcome) {
      case 'exact':
        return 'border-amber-900/20 dark:border-amber-950/30';
      case 'correct':
        return 'border-green-600/25 dark:border-green-400/30';
      case 'wrong':
        return 'border-red-600/25 dark:border-red-400/30';
    }
  }

  switch (outcome) {
    case 'missed':
      return 'border-gray-500/40 dark:border-gray-500';
    default:
      return 'border-[var(--border)]';
  }
}

export function getOutcomeMutedTextColor(outcome: PredictionOutcome): string {
  if (isScoredOutcome(outcome)) {
    switch (outcome) {
      case 'exact':
        return 'text-amber-950/70 dark:text-amber-950/80';
      case 'correct':
        return 'text-green-800/70 dark:text-green-100/70';
      case 'wrong':
        return 'text-red-800/70 dark:text-red-100/70';
    }
  }

  return 'text-[var(--text-secondary)]';
}
